import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { AlertCircle, Mic, TrendingUp, Clock } from "lucide-react";
import BRAND from "../brand";

export default function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    const result = await register(email, password, name);
    if (!result.success) {
      setError(result.error);
    } else if (result.pending) {
      navigate("/pending-approval");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Left Side - Premium Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-navy-gradient text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3B] via-[#0B1F3B] to-[#1e3a5f] opacity-95"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#2563EB]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#14B8A6]/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-2">
            <img 
              src={BRAND.logoUrl} 
              alt="Revyniqx" 
              className="h-10 w-auto"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <div className="mt-4">
            <h1 className="text-3xl font-bold tracking-tight">{BRAND.productName}</h1>
            <p className="text-blue-200/80 text-sm mt-1">{BRAND.productTagline}</p>
          </div>
        </div>
        
        <div className="relative z-10 space-y-10">
          <div>
            <h2 className="text-4xl font-bold leading-tight mb-4">
              Start Your Journey to<br />
              <span className="text-[#14B8A6]">Sales Excellence</span>
            </h2>
            <p className="text-blue-100/80 text-lg max-w-md">
              Join thousands of sales professionals who are mastering customer conversations with AI-powered practice.
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl backdrop-blur-sm">
              <div className="w-12 h-12 bg-[#14B8A6]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Mic className="w-6 h-6 text-[#14B8A6]" />
              </div>
              <div>
                <h3 className="font-semibold">Voice-First Practice</h3>
                <p className="text-sm text-blue-200/60">Practice real conversations in Hindi, English, or Hinglish</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl backdrop-blur-sm">
              <div className="w-12 h-12 bg-[#2563EB]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-[#2563EB]" />
              </div>
              <div>
                <h3 className="font-semibold">Track Your Progress</h3>
                <p className="text-sm text-blue-200/60">Get detailed coaching insights and readiness scores</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 space-y-2">
          <p className="text-sm text-blue-200/50">{BRAND.poweredBy}</p>
          <p className="text-xs text-blue-200/30">{BRAND.companyAttribution}</p>
        </div>
      </div>

      {/* Right Side - Register Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img 
              src={BRAND.logoUrl} 
              alt="Revyniqx" 
              className="h-8 w-auto mb-3"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h1 className="text-xl font-bold text-[#0B1F3B]">{BRAND.productName}</h1>
            <p className="text-sm text-slate-500">{BRAND.productTagline}</p>
          </div>

          <Card className="shadow-xl border-slate-200/60 rounded-2xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold text-[#0B1F3B]">Create your account</CardTitle>
              <CardDescription className="text-slate-500">Start your sales practice journey</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-xl text-sm" data-testid="register-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium text-slate-700">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="h-11 rounded-xl border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]"
                    data-testid="register-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11 rounded-xl border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]"
                    data-testid="register-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 rounded-xl border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]"
                    data-testid="register-password-input"
                  />
                  <p className="text-xs text-slate-400">Minimum 6 characters</p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 rounded-xl btn-premium" 
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? "Creating account..." : "Create account"}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm text-slate-500">
                Already have an account?{" "}
                <Link to="/login" className="text-[#2563EB] hover:underline font-medium" data-testid="login-link">
                  Sign in
                </Link>
              </div>
            </CardContent>
          </Card>

          <div className="lg:hidden mt-8 text-center space-y-1">
            <p className="text-xs text-slate-400">{BRAND.poweredBy}</p>
            <p className="text-xs text-slate-300">{BRAND.companyAttribution}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
